/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import type { Workbook, Worksheet, Cell, Column } from 'exceljs';
type ExcelJSModule = typeof import('exceljs');
type SaveAsFn = (data: Blob | File | string, filename?: string, options?: unknown) => void;

const HEADER_STYLE = {
  font: { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 },
  fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0657A8' } },
  alignment: { horizontal: 'center', vertical: 'middle' },
  border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } },
} as const;

@Injectable({ providedIn: 'root' })
export class ExcelService {
  private excelJsWorkbookConstructor?: Promise<ExcelJSModule['Workbook']>;
  private saveAsFunction?: Promise<SaveAsFn>;

  private getWorkbookConstructor(): Promise<ExcelJSModule['Workbook']> {
    this.excelJsWorkbookConstructor ||= import('exceljs').then(mod => mod.Workbook);
    return this.excelJsWorkbookConstructor;
  }

  private getSaveAsFunction(): Promise<SaveAsFn> {
    this.saveAsFunction ||= import('file-saver').then((mod: any) =>
      (mod.default ?? mod.saveAs ?? mod) as SaveAsFn
    );
    return this.saveAsFunction;
  }

  async createWorkbook(): Promise<Workbook> {
    const WorkbookConstructor = await this.getWorkbookConstructor();
    return new WorkbookConstructor();
  }

  createWorksheet(workbook: Workbook, title: string): Worksheet {
    const worksheet = workbook.addWorksheet(title);
    worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    return worksheet;
  }

  addHeaders(worksheet: Worksheet, headers: string[]): void {
    const headerRow = worksheet.addRow(headers);
    headerRow.height = 25;
    headerRow.eachCell(cell => {
      cell.style = HEADER_STYLE;
    });
  }

  addData(worksheet: Worksheet, data: (string | number | Date | null)[][]): void {
    data.forEach((rowData, index) => {
      const row = worksheet.addRow(rowData);
      if ((index + 1) % 2 === 0) {
        row.eachCell({ includeEmpty: true }, (cell: Cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF0F8FF' },
          };
        });
      }
    });
  }

  autoFitColumns(worksheet: Worksheet): void {
    worksheet.columns.forEach((column: Partial<Column>) => {
      if (!column.eachCell) return;

      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell: Cell) => {
        const cellLength = cell.value?.toString().length ?? 0;
        if (cellLength > maxLength) {
          maxLength = cellLength;
        }
      });
      column.width = Math.max(12, maxLength + 4);
    });
  }

 async generateExcelFile(workbook: Workbook, filename: string): Promise<void> {
    const saveAs = await this.getSaveAsFunction();
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${filename}.xlsx`);
  }
}
